/**
 * LocalStorageLevelService manages log levels using `localStorage`.
 * It supports both `loglevel-patterns` (a unified JSON with patterns) and individual keys like `loglevel:nizza:core:*`.
 * Prioritizes specific patterns over general ones and implements override logic.
 *
 * Examples of usage:
 *
 * 1. Set a log level for a specific context:
 *    const service = new LocalStorageLevelService('nizza');
 *    service.setLevel('nizza:core:test', 'DEBUG');
 *    service.setLevel('nizza:core:*', 'ERROR!'); // Override for all core-related logs
 *
 * 2. Retrieve a log level for a context:
 *    const level = service.getLevel('nizza:core:test'); // Resolves to "DEBUG"
 *    const level2 = service.getLevel('nizza:other:context');   // Falls back to "INFO" or default level
 *
 * 3. Configure patterns via `loglevel-patterns`:
 *    localStorage.setItem('loglevel-patterns', JSON.stringify({
 *      'nizza:core:*': { level: 'ERROR' },
 *      '*:core:test': { level: 'DEBUG', override: true }
 *    }));
 *    const level3 = service.getLevel('nizza:core:test'); // Resolves to "DEBUG" due to override
 */

import picomatch from 'picomatch';
import { LogLevel } from '../../../constants';
import { LevelService } from '../../types';
import { InvalidLogLevelError, isValidLogType } from '../utils';

export const LOGLEVEL_PATTERNS_KEY = 'loglevel-patterns' as const;
export const LOGLEVEL_PREFIX = 'loglevel:' as const;
export const OVERRIDE_SUFFIX = '!' as const;
const DEFAULT_LEVEL = import.meta.env.PROD ? LogLevel.INFO : LogLevel.DEBUG;

interface LogPatternEntry {
  level: LogLevel;
  override?: boolean;
}

type LogPatternMap = Record<string, LogPatternEntry>;

export class LocalStorageLevelService implements LevelService {
  private readonly projectPrefix: string;
  private patternsCache: LogPatternMap | null = null;

  /**
   * Initializes the service with a specific project name.
   * @param project - The project for which log levels are managed.
   */
  constructor(project: string) {
    this.projectPrefix = `${LOGLEVEL_PREFIX}${project}:`;
  }

  /**
   * Retrieves the log level for a given context.
   * Resolves the most specific match, considering override rules.
   * @param context - The specific context to retrieve the log level for.
   * @returns The resolved log level.
   */
  getLevel(context: string): LogLevel {
    const patterns = this.loadAndUnifyPatterns();
    return this.resolveLogLevelFromPatterns(context, patterns) || DEFAULT_LEVEL;
  }

  /**
   * Sets the log level for a specific context as an individual key.
   * @param context - The specific context for which to set the log level.
   * @param level - The log level to set, optionally with "!".
   */
  setLevel(
    context: string,
    level: LogLevel | `${LogLevel}${typeof OVERRIDE_SUFFIX}`,
  ): void {
    const { level: validLevel, override } = this.parseLogLevel(level);
    const key = `${LOGLEVEL_PREFIX}${context}`;

    // Reconstructs the value to store (add OVERRIDE_SUFFIX if it is override)
    const storedValue = override
      ? `${validLevel}${OVERRIDE_SUFFIX}`
      : validLevel;
    localStorage.setItem(key, storedValue);

    // Having modified localStorage, we restart the cache to read again
    this.patternsCache = null;
  }

  /**
   * Loads and unifies patterns from both loglevel-patterns and individual keys.
   * Ensures the process is done only once per app lifecycle.
   * @returns A unified map of patterns.
   */
  private loadAndUnifyPatterns(): LogPatternMap {
    if (this.patternsCache) return this.patternsCache;

    const basePatterns = this.readPatternsFromJSON();
    const unifiedPatterns = this.unifyWithIndividualKeys(basePatterns);
    this.patternsCache = unifiedPatterns;
    return unifiedPatterns;
  }

  /**
   * Resolves the log level for a context using the unified patterns.
   * Handles override logic and prioritizes more specific matches.
   * @param context - The context to resolve the log level for.
   * @param patterns - The unified patterns map.
   * @returns The resolved log level, or undefined if no match is found.
   */
  private resolveLogLevelFromPatterns(
    context: string,
    patterns: LogPatternMap,
  ): LogLevel | undefined {
    let matchedLevel: LogLevel | undefined;
    const overrideFound = false;

    for (const [pattern, { level, override }] of Object.entries(patterns)) {
      if (picomatch(pattern.toLowerCase())(context.toLowerCase())) {
        // Immediate return if override is found
        if (override) return level;

        // Prioritize the most specific match
        if (!matchedLevel || (picomatch(pattern)(context) && !overrideFound)) {
          matchedLevel = level;
        }
      }
    }

    return matchedLevel;
  }

  /**
   * Reads patterns from loglevel-patterns in localStorage.
   * @returns The parsed patterns map.
   */
  private readPatternsFromJSON(): LogPatternMap {
    try {
      const raw = localStorage.getItem(LOGLEVEL_PATTERNS_KEY);
      return raw ? (JSON.parse(raw) as LogPatternMap) : {};
    } catch {
      console.error('Failed to parse loglevel-patterns from localStorage');
      return {};
    }
  }

  /**
   * Unifies patterns from loglevel-patterns with individual keys from localStorage.
   * @param basePatterns - The base patterns loaded from loglevel-patterns.
   * @returns A unified map of patterns.
   */
  private unifyWithIndividualKeys(basePatterns: LogPatternMap): LogPatternMap {
    const unifiedPatterns = { ...basePatterns };

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        !key ||
        !key.startsWith(LOGLEVEL_PREFIX) ||
        key === LOGLEVEL_PATTERNS_KEY
      )
        continue;

      const pattern = key.replace(LOGLEVEL_PREFIX, '').toLowerCase();
      const value = localStorage.getItem(key);
      if (!value) continue;

      const { level, override } = this.parseLogLevel(value);
      unifiedPatterns[pattern] = { level, override };
    }

    return unifiedPatterns;
  }

  /**
   * Parses a log level string, identifying if it's an override.
   * @param value - The value to parse (e.g., "ERROR!").
   * @returns An object with the level and override flag.
   */
  private parseLogLevel(value: string): LogPatternEntry {
    const isOverride = value.endsWith(OVERRIDE_SUFFIX);
    const level = isOverride
      ? (value.slice(0, -OVERRIDE_SUFFIX.length) as LogLevel)
      : (value as LogLevel);

    if (!isValidLogType(level)) {
      throw new InvalidLogLevelError(level);
    }

    return { level, override: isOverride };
  }
}
